import { isAdminLike } from './roles';

// Shared vocabulary for a REJECTED price write (single SET, AI accept, or one
// row of a bulk apply). Pricers reported prices "silently reverting": the write
// was refused by our own backend (per-user drop limit, stale-price 409, rate
// limiter) and a 3-second toast was the only sign. Callers turn this into a
// sticky per-row marker instead.
//
// Nothing here ever re-sends: a limit/409 needs a different price, and a 429 or
// an unknown outcome must not turn into a second write (the server may have
// applied the first one).

// A proxy timeout or a dead connection does NOT mean the write failed — the
// backend keeps applying after nginx gives up (same rule as BulkPriceModal).
const UNKNOWN_STATUSES = new Set([502, 504]);

const parseRetryAfter = (err) => {
  const body = Number(err?.response?.data?.retryAfter);
  const header = Number(err?.response?.headers?.['retry-after']);
  const secs = Number.isFinite(body) && body > 0
    ? body
    : (Number.isFinite(header) && header > 0 ? header : 30);
  return Math.min(300, Math.max(1, Math.ceil(secs)));
};

/**
 * @returns {{ message: string, status: number|'unknown', code: string|null, retryAfterSec: number|null }}
 */
export function describePriceError(err) {
  const status = err?.response?.status;
  const data = err?.response?.data || {};
  const code = data.code || null;

  if (!err?.response || UNKNOWN_STATUSES.has(status) || err?.code === 'ECONNABORTED') {
    return {
      status: 'unknown',
      code,
      retryAfterSec: null,
      message: 'Timed out — the server may still be applying this price. Refresh before retrying.',
    };
  }

  if (status === 429) {
    const retryAfterSec = parseRetryAfter(err);
    return {
      status,
      code,
      retryAfterSec,
      message: data.error || 'Too many price updates in a row — this one was not sent.',
    };
  }

  return {
    status: status ?? null,
    code,
    retryAfterSec: null,
    message: data.error || err?.message || 'Failed to update price',
  };
}

/**
 * Tooltip for a price input: the limits this user's writes are checked against
 * server-side (services/priceUpdate.js checkPriceLimits). Admin-level roles are
 * unlimited, so they get nothing.
 */
export function priceLimitsHint(user) {
  if (!user || isAdminLike(user.role)) return null;
  // Same fallbacks as the backend, so the hint matches what actually rejects.
  const down = Number.isFinite(Number(user.maxPriceChangePercent)) ? Number(user.maxPriceChangePercent) : 50;
  const up = Number.isFinite(Number(user.maxPriceIncreasePercent)) ? Number(user.maxPriceIncreasePercent) : 300;
  const min = Number(user.minPrice);
  const max = Number(user.maxPrice);
  const range = Number.isFinite(min) && Number.isFinite(max) ? ` · $${min}–$${max}` : '';
  return `Your limits: -${down}% / +${up}% per change${range}`;
}
